import Sidebar from '../components/dashboard-layout/Sidebar';
import { Link, useLocation, Navigate, useNavigate } from '@tanstack/react-router';
import { SidebarItem } from '../components/dashboard-layout/Sidebar';
import {
    House,
    Calendar,
    MessageSquare,
    ChartPie,
    MoreHorizontal,
    Award,
    CreditCard,
    Table2,
    CirclePlus,
    User,
    Sparkles,
    Plus,
} from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useToken } from '../hooks/useToken';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
// SessionLoading intentionally removed — not used in this file
import TopBar from '../components/dashboard-layout/TopBar';
import { useState, useEffect, useRef, useContext } from 'react';
import DashboardWrapperContext from '../contexts/DashboardWrapperContext';
import { useUser } from '@clerk/clerk-react';
// axios removed — use centralized api helpers where needed
import FloatingChatWidget from '../pages/ChatPage/FloatingChatWidget';

// DashboardWrapperContext is imported from ../contexts/DashboardWrapperContext

const DashboardWrapper = ({ children }) => {
    const inside = useContext(DashboardWrapperContext);
    const { isLoaded, isSignedIn } = useAuth();
    const { user } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    // streak info is derived from query below

    const token = useToken();

    const { data: streakInfo } = useQuery({
        queryKey: queryKeys.userStreak(user?.id),
        queryFn: () => api.getUserStreak(user?.id, token || ''),
        enabled: !!token && !!user?.id && isSignedIn,
        select: d => {
            const streakData = d?.user_streak;
            if (!streakData) return { currentStreak: 0, longestStreak: 0, activeToday: false };
            if (streakData.streak_string) {
                const xpPerDay = streakData.streak_string.split(',').map(Number);
                const getDayOfYear = date => {
                    const start = new Date(date.getFullYear(), 0, 0);
                    const diff = date - start;
                    const oneDay = 1000 * 60 * 60 * 24;
                    return Math.floor(diff / oneDay);
                };
                const todayIndex = getDayOfYear(new Date()) - 1;
                const activeToday = todayIndex < xpPerDay.length && xpPerDay[todayIndex] > 0;

                // compute current streak
                let currentStreak = 0;
                if (todayIndex < xpPerDay.length && xpPerDay[todayIndex] > 0) {
                    currentStreak = 1;
                    for (let i = todayIndex - 1; i >= 0; i--) {
                        if (xpPerDay[i] > 0) currentStreak++;
                        else break;
                    }
                }

                // compute longest
                let longestStreak = 0;
                let currentLongest = 0;
                for (let i = 0; i < xpPerDay.length; i++) {
                    if (xpPerDay[i] > 0) currentLongest++;
                    else {
                        longestStreak = Math.max(longestStreak, currentLongest);
                        currentLongest = 0;
                    }
                }
                longestStreak = Math.max(longestStreak, currentLongest);

                return { currentStreak, longestStreak, activeToday };
            }
            return {
                currentStreak: streakData.current_streak || 0,
                longestStreak: streakData.longest_streak || 0,
                activeToday: (streakData.current_streak || 0) > 0,
            };
        },
    });

    const streakNumber = streakInfo?.currentStreak ?? 0;
    const streakStatus = streakInfo?.activeToday ? 'on' : 'off';

    const [expanded, setExpanded] = useState(() => {
        const savedState = localStorage.getItem('sidebarExpanded');
        return savedState !== null ? JSON.parse(savedState) : window.innerWidth > 1100;
    });
    const prevPathRef = useRef(location.pathname);

    useEffect(() => {
        // Collapse sidebar on screens below 1100px
        const handleResize = () => {
            if (window.innerWidth < 1100) {
                setExpanded(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Don't expand sidebar when navigating between pages
        if (prevPathRef.current !== location.pathname) {
            setExpanded(false);
            prevPathRef.current = location.pathname;
        }
    }, [location.pathname]);

    useEffect(() => {
        localStorage.setItem('sidebarExpanded', JSON.stringify(expanded));
    }, [expanded]);

    // no manual fetch; `useQuery` below keeps streak data in sync

    const toggleSidebar = () => {
        setExpanded(curr => !curr);
    };

    // Navigation functions for More dropdown
    const navigateToBilling = async () => {
        await navigate({ to: '/billing' });
    };

    const navigateToProfile = async () => {
        await navigate({ to: '/profile' });
    };
    const navigateToLeaderboard = async () => {
        await navigate({ to: '/leaderboard' });
    };

    const navigateToExploreCourses = async () => {
        await navigate({ to: '/explore-courses' });
    };

    // Route state checks for More dropdown
    const isOnBillingPage = location.pathname === '/billing';
    const isOnProfilePage = location.pathname === '/profile';
    const isOnLeaderboardPage = location.pathname === '/leaderboard';
    const isOnExploreCoursesPage = location.pathname === '/explore-courses';

    // More dropdown items configuration
    const moreItems = [
        {
            icon: <CirclePlus className="w-5 h-5 text-neutral-700" />,
            label: 'Explore Courses',
            onClick: navigateToExploreCourses,
            active: isOnExploreCoursesPage,
        },
        {
            icon: <Table2 className="w-5 h-5 text-neutral-700" />,
            label: 'View Leaderboards',
            onClick: navigateToLeaderboard,
            active: isOnLeaderboardPage,
        },
        // {
        //     icon: <CreditCard className="w-5 h-5 text-neutral-700" />,
        //     label: 'My Billing/Finance',
        //     onClick: navigateToBilling,
        //     active: isOnBillingPage,
        // },
        // {
        //     icon: <User className="w-5 h-5 text-neutral-700" />,
        //     label: 'My Profile',
        //     onClick: navigateToProfile,
        //     active: isOnProfilePage,
        // },
    ];

    // If auth is still loading, render the chrome immediately with a subtle skeleton
    // in the main content area so the page never goes blank while Clerk initialises.
    const mainPadding = expanded ? 'md:pl-61' : 'md:pl-18';

    // If nested inside another DashboardWrapper, avoid duplicating TopBar/Sidebar.
    if (inside) {
        if (!isLoaded) {
            return (
                <div className="mx-auto w-full grow max-w-325 py-5 px-4 md:px-8 max-md:py-20 md:mt-20 bg-neutral-50">
                    <div className="animate-pulse py-4">
                        <div className="h-8 bg-neutral-200 rounded w-1/3" />
                        <div className="h-4 bg-neutral-200 rounded w-1/2" />
                        <div className="h-48 bg-neutral-200 rounded-2xl" />
                    </div>
                </div>
            );
        }
        return children;
    }

    // Wait for Clerk to finish loading before deciding to redirect.
    // This prevents a brief unauthenticated state during page refresh
    // from immediately navigating to the sign-in page.
    if (!isLoaded) {
        return (
            <div className="mx-auto w-full grow max-w-325 py-5 px-4 md:px-8 max-md:py-20 md:mt-20 bg-neutral-50">
                <div className="animate-pulse py-4">
                    <div className="h-8 bg-neutral-200 rounded w-1/3" />
                    <div className="h-4 bg-neutral-200 rounded w-1/2" />
                    <div className="h-48 bg-neutral-200 rounded-2xl" />
                </div>
            </div>
        );
    }

    if (!isSignedIn) {
        return <Navigate to="/sign-in" search={{ from: location.pathname }} replace />;
    }

    return (
        <DashboardWrapperContext.Provider value={true}>
            <div className="flex flex-col md:flex-row bg-neutral-50">
                <TopBar
                    expanded={expanded}
                    streakStatus={streakStatus}
                    streakNumber={streakNumber}
                    toggleSidebar={toggleSidebar}
                />
                <Sidebar expanded={expanded} moreItems={moreItems}>
                    <Link to="/chat" preload="intent" preloadDelay={0}>
                        <SidebarItem
                            icon={
                                <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
                                    <Plus className="w-4 h-4 text-primary" />
                                </div>
                            }
                            text={<span className="text-primary font-medium">New AI Chat</span>}
                            mobileText="AI"
                            active={location.pathname === '/chat'}
                            desktopOnly
                        />
                    </Link>
                    <Link to="/dashboard" preload="intent" preloadDelay={0}>
                        <SidebarItem icon={<House />} text="Home" mobileText="Home" active={location.pathname === '/dashboard'} />
                    </Link>
                    <Link to="/live-classes" preload="intent" preloadDelay={0}>
                        <SidebarItem
                            icon={<Calendar />}
                            text="My Classes"
                            mobileText="Classes"
                            active={location.pathname === '/live-classes'}
                        />
                    </Link>
                    <Link to="/project-feedback" preload="intent" preloadDelay={0}>
                        <SidebarItem
                            icon={<MessageSquare />}
                            text="Assignments"
                            mobileText="Project"
                            active={location.pathname === '/project-feedback'}
                        />
                    </Link>
                    <Link to="/track-progress" preload="intent" preloadDelay={0}>
                        <SidebarItem
                            icon={<ChartPie />}
                            text="Track My Progress"
                            mobileText="Progress"
                            active={location.pathname === '/track-progress'}
                        />
                    </Link>

                    <Link to="/achievements" preload="intent" preloadDelay={0}>
                        <SidebarItem
                            icon={<Award />}
                            text={'My Achievements'}
                            active={location.pathname === '/achievements'}
                            desktopOnly
                        />
                    </Link>
                    <Link to="/more" preload="intent" preloadDelay={0}>
                        <SidebarItem icon={<MoreHorizontal />} text={'More'} active={location.pathname === '/more'} mobileOnly />
                    </Link>
                </Sidebar>
                <main
                    className={`flex flex-col justify-center items-center w-full min-h-screen pb-16 md:pb-0 mx-2 md:mx-8 min-[1300px]:mx-0 bg-neutral-50 ${mainPadding}`}
                >
                    <div className="mx-auto w-full grow max-w-325 py-5 px-4 md:px-8 max-md:py-20 md:mt-20 bg-neutral-50">
                        {children}
                    </div>
                </main>
                <div className="max-lg:hidden">
                    <FloatingChatWidget />
                </div>
            </div>
        </DashboardWrapperContext.Provider>
    );
};

export default DashboardWrapper;
